import React, { useCallback, useEffect, useRef, useState } from "react";
import { askSop, getSopCoverage } from "../../../api/sopsApi";
import "./sop-ask.css";

/* Ask about this procedure — a read-only conversation with ONE document.

   Same shape as the review conversation, but it answers rather than edits:
   nothing here writes, and no turn changes the SOP.

   Three properties belong to the endpoint (agent_actions/sop_qa), and this panel
   exists partly to make sure none of them get softened on the way to the screen:

   GROUNDING — `grounded: false` means this document does not settle the
   question. That answer renders as-is, flagged, and is never dressed up as an
   answer or quietly widened to the rest of the library. An employee acts on
   what this says, so an invented notice period is worse than no answer.

   CITATION — the section and version the text came from, under every answer, so
   it can be read and checked against the document on the same screen. An answer
   without its source is a claim.

   DRAFT — a manager reviewing an unpublished SOP can ask about it, and the
   answer says it is a draft. A draft is not policy yet. */

export default function SopAskPanel({ slug, title }) {
  const [question, setQuestion] = useState("");
  const [turns, setTurns] = useState([]);
  const [covers, setCovers] = useState([]);
  const [busy, setBusy] = useState(false);
  const threadRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setTurns([]);
    setCovers([]);
    getSopCoverage(slug)
      .then((res) => {
        if (!cancelled) setCovers(res?.data?.covers || []);
      })
      .catch(() => {
        /* what the document covers is orientation, not the answer path */
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const ask = useCallback(
    async (text) => {
      const q = String(text || "").trim();
      if (!q || busy) return;
      setQuestion("");
      setBusy(true);
      setTurns((prev) => [...prev, { question: q, pending: true }]);
      try {
        const res = await askSop(slug, q);
        setTurns((prev) => prev.map((t, i) => (i === prev.length - 1 ? { question: q, answer: res.data } : t)));
      } catch (err) {
        const detail =
          err?.response?.data?.error || err?.message || "Could not reach the procedure right now.";
        setTurns((prev) => prev.map((t, i) => (i === prev.length - 1 ? { question: q, error: detail } : t)));
      } finally {
        setBusy(false);
        requestAnimationFrame(() => {
          if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
        });
      }
    },
    [slug, busy]
  );

  return (
    <section className="sop-ask" aria-label={`Ask about ${title || "this procedure"}`}>
      <header className="sop-ask-head">
        <h3>Ask about this procedure</h3>
        <span className="sop-ask-note">Answered from this document only</span>
      </header>

      {turns.length === 0 ? (
        <div className="sop-ask-intro">
          <p>
            If the procedure doesn&apos;t say, it will tell you that rather than guess. Every answer
            names the section it came from.
          </p>
          {covers.length > 0 && (
            <div className="sop-ask-chips">
              {covers.slice(0, 4).map((heading) => (
                <button key={heading} type="button" className="sop-ask-chip" onClick={() => ask(heading)}>
                  {heading}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="sop-ask-thread" ref={threadRef}>
          {turns.map((t, i) => (
            <div className="sop-ask-turn" key={`${i}-${t.question}`}>
              <div className="sop-ask-q">{t.question}</div>

              {t.pending && <div className="sop-ask-pending">Reading the procedure…</div>}

              {t.error && <div className="sop-ask-a is-error">{t.error}</div>}

              {t.answer && (
                <div className={`sop-ask-a${t.answer.grounded ? "" : " is-ungrounded"}`}>
                  {!t.answer.grounded && <span className="sop-ask-flag">Not in this procedure</span>}
                  {t.answer.sop && !t.answer.sop.is_published && (
                    <span className="sop-ask-flag is-draft">From a draft — not policy yet</span>
                  )}
                  <p className="sop-ask-text">{t.answer.answer}</p>
                  {t.answer.citation && <p className="sop-ask-cite">{t.answer.citation}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <form
        className="sop-ask-composer"
        onSubmit={(e) => {
          e.preventDefault();
          ask(question);
        }}
      >
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="What do you want to know?"
          disabled={busy}
          aria-label="Your question about this procedure"
        />
        <button type="submit" className="ops-btn" disabled={busy || !question.trim()}>
          {busy ? "…" : "Ask"}
        </button>
      </form>
    </section>
  );
}
